export type BuddyRelationshipState =
  | 'accepted'
  | 'incoming'
  | 'outgoing'
  | 'checking'
  | 'none';

export const BUDDY_RELATION_ACTION = {
  request: 'BuddyInvite',
  removeBuddy: 'RemoveBuddy',
} as const;

interface BuddyRelationshipSource {
  isBuddy?: boolean;
  isAccepted?: boolean;
  /** The other member invited the current viewer. */
  invited?: boolean;
  /** The current viewer already requested the other member. */
  isRequested?: boolean;
  status?: string | null;
  hasLocallySentRequest?: boolean;
  incomingLookup?: boolean | null;
  checkingIncoming?: boolean;
  override?: BuddyRelationshipState | null;
}

/**
 * Keeps request direction intact. Incoming requests can be accepted/rejected;
 * only outgoing requests can be cancelled.
 */
export function resolveBuddyRelationshipState({
  isBuddy,
  isAccepted,
  invited,
  isRequested,
  status,
  hasLocallySentRequest,
  incomingLookup,
  checkingIncoming,
  override,
}: BuddyRelationshipSource): BuddyRelationshipState {
  const normalizedStatus = status?.trim().toLowerCase() || '';
  // A response to an action made on this page wins until the server catches
  // up. An outgoing request, however, must yield when the other member accepts
  // or rejects it in another browser.
  if (override === 'accepted' || override === 'none') return override;
  if (isBuddy || isAccepted || normalizedStatus === 'accepted') {
    return 'accepted';
  }
  if (normalizedStatus === 'declined' || normalizedStatus === 'rejected') {
    return 'none';
  }
  // The viewer's Requested list is authoritative for request direction and
  // must beat stale per-tab hints or ambiguous profile flags.
  if (incomingLookup === true) return 'incoming';
  if (checkingIncoming) return 'checking';
  if (override) return override;
  if (hasLocallySentRequest) return 'outgoing';
  if (invited) return 'incoming';
  if (
    isRequested ||
    (normalizedStatus === 'pending' && incomingLookup === false)
  ) {
    return 'outgoing';
  }
  return 'none';
}
